<template>
  <div class="mortgage-documents-page">
    <loader v-if="loading" />
    <div v-else class="padding-l">
      <div class="header-section">
        <h1 class="ri-small-display">מסמכי משכנתא</h1>
      </div>

      <section v-if="requiredDocuments.length > 0" class="documents-section margin-top-l">
        <div class="section-title ri-large-body margin-bottom-sm">
          <span class="ri-font-weight-bold">מסמכים שצריך להעלות</span>
          <span class="section-number">{{ documentsToUploadSectionNumber }}</span>
        </div>
        <document-card
          v-for="document in requiredDocuments"
          :key="getDocumentKey(document)"
          :document="document"
          @upload="handleUpload"
        />
      </section>

      <section v-if="uploadedDocuments.length > 0" class="documents-section margin-top-xl">
        <div class="section-title ri-large-body margin-bottom-sm">
          <span class="ri-font-weight-bold">מסמכים קיימים</span>
          <span class="section-number">{{ uploadedDocumentsSectionNumber }}</span>
        </div>
        <document-card
          v-for="document in uploadedDocuments"
          :key="getDocumentKey(document)"
          :document="document"
          @upload="handleUpload"
        />
      </section>
    </div>

    <!-- File upload input -->
    <input
      ref="fileInput"
      type="file"
      accept=".pdf,.jpg,.jpeg,.png"
      style="display: none;"
      @change="onFileSelected"
    />
  </div>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import { mapActions, mapGetters, mapState } from 'vuex';
import Segment from '@/Segment';
import mortgagesApi from '@/api/MortgagesApi';
import DocumentCard from './components/DocumentCard.vue';

export default {
  name: 'MortgageDocumentsCollectionPage',
  components: {
    Loader: BaseUI.TinyLoader,
    DocumentCard,
  },
  data() {
    return {
      selectedDocument: null,
      uploadingFile: false,
    };
  },
  async created() {
    Segment.trackUserInteraction('MortgageDocumentsCollectionPage_Entered');
    await this.fetchMortgageDocuments();
    this.setAppHeaderProps({
      backgroundColorVariant: BaseUI.LayoutConsts.COLOR_VARIANT.BEIGE,
      textColor: BaseUI.Colors.riseupBlack,
      logoColor: BaseUI.Colors.riseupBlack,
      bottomBorder: false,
    });
  },
  computed: {
    ...mapState('mortgageDocuments', ['loading', 'documents']),
    ...mapGetters('mortgageDocuments', ['requiredDocuments', 'uploadedDocuments']),
    documentsToUploadSectionNumber() {
      return this.requiredDocuments.length;
    },
    uploadedDocumentsSectionNumber() {
      return this.uploadedDocuments.length;
    },
  },
  methods: {
    ...mapActions('mortgageDocuments', ['fetchMortgageDocuments']),
    ...mapActions('appHeader', ['setAppHeaderProps']),
    getDocumentKey(document) {
      return `${document.documentType}-${document.borrowerIndex}-${document.sequence || 1}`;
    },
    handleUpload(document) {
      this.selectedDocument = document;
      this.$refs.fileInput.click();
    },
    async onFileSelected(event) {
      const file = event.target.files[0];

      if (!file) {
        return;
      }

      await this.uploadFile(file);
      this.$refs.fileInput.value = '';
    },
    async uploadFile(file) {
      this.uploadingFile = true;
      try {
        const arrayBuffer = await file.arrayBuffer();
        const buffer = Buffer.from(arrayBuffer);
        const { documentType, borrowerIndex } = this.selectedDocument;
        await mortgagesApi.uploadMortgageDocumentFile(documentType, borrowerIndex, buffer, file.name, file.type);

        Segment.trackUserInteraction('MortgageDocumentsCollectionPage_FileUploaded', {
          documentType,
          borrowerIndex,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });

        await this.fetchMortgageDocuments();
      } catch (error) {
        console.error('File upload error:', error);
        Segment.trackUserInteraction('MortgageDocumentsCollectionPage_FileUploadError', {
          error: error.message,
          status: error.response?.status,
          documentType: this.selectedDocument?.documentType,
        });
      } finally {
        this.uploadingFile = false;
        this.selectedDocument = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@riseupil/base-ui/src/scss/riseup-colors";
@import "~@riseupil/base-ui/src/scss/spacings";

.mortgage-documents-page {
  background: #FCF7ED;
  flex: 1;
  .header-section {
    text-align: right;
  }

  .documents-section {
    .section-title {
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
